import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid2,
  Tooltip,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  parseQueryParamsForOpsAnalyticsFilters,
  OpsAnalyticsFilters,
  OpsAnalyticsSelectedFilters,
} from "../../../models/Dashboard/OpsDashboard/OpsAnalyticsFilters";
import rawInward from "../../../assets/rawinward.png";
import rawQc from "../../../assets/rawqc.png";
import printOutward from "../../../assets/outward.png";
import printInward from "../../../assets/printinward.png";
import printQc from "../../../assets/setting.png";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import TypeFilterCard from "../../components/filters/TypeFilterCard";
import LineChartComponent from "../../components/charts/AppLineChart";
import PieChartComponent from "../../components/charts/AppPieChart";
import TwoPieChartsComponent from "../../components/charts/AppPieChartcircle";
// import AppStackChart from "../../components/charts/AppStackChart";
import AppCombinedChart from "../../components/charts/AppAreaBarChart";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import prCreated from "../../../assets/msgboxblue.png";
import poCreated from "../../../assets/approvedredicon.png";
import mockuplod from "../../../assets/upload.png";
import mockapproval from "../../../assets/approval.png";
import mockpntr from "../../../assets/printer.png";
import DashboardOrderTable from "../../components/tables/DashboardOrdersTable";
import AreaChartComponent from "../../components/charts/AppAreaChart";
import { Admin } from "../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import AppStackChart from "../../components/charts/AppStackChart";
import { SortType } from "../../../models/SortType";
import AppNoFocusButton from "../../components/common/buttons/AppNoFocusButton";

type Filter = {
  type: string;
  value: string;
};

const excludedOrdersOptions = [
  {
    id: "false",
    value: "False",
  },
  {
    id: "true",
    value: "True",
  },
];

const OPS_ANALYTICS_FILTERS_KEY = "OPS_ANALYTICS_FILTERS_KEY";

const OpsAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    opsAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<Filter[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloadList, setReloadList] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: OpsAnalyticsFilters =
    parseQueryParamsForOpsAnalyticsFilters(
      localStorage.getItem(OPS_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(OPS_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] = useState<OpsAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<OpsAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reloadList) reloadOrdersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(OPS_ANALYTICS_FILTERS_KEY, JSON.stringify(filters));

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    if (
      selectedFilters.page !== filters.page ||
      selectedFilters.sortBy !== filters.sortBy ||
      selectedFilters.sortType !== filters.sortType
    ) {
      setReloadList(true);
    } else {
      setReloadList(false);
    }

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<Filter[]> = [];

    if (filters.pm_adminids) {
      filters.pm_adminids.forEach((pm_adminids) =>
        newFilters.push({ type: "pm_adminids", value: pm_adminids.value })
      );
    }

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ type: "order_id", value: order_id.value })
      );
    }
    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ type: "order_type", value: order_type.value })
      );
    }
    if (filters.archived) {
      filters.archived.forEach((archived) =>
        newFilters.push({ type: "archived", value: archived.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await opsAnalyticsDashboardStore.getAllAnalyticsData({
        pm_adminids: selectedFilters.pm_adminids,
        archived: selectedFilters.archived,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
      });

      await opsAnalyticsDashboardStore.getOrderListTableData({
        pm_adminids: selectedFilters.pm_adminids,
        archived: selectedFilters.archived,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        limit: 30,
        search: "",
        page: 1,
        sortBy: "created_date",
        sortType: SortType.DESC,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const reloadOrdersList = async () => {
    try {
      setLoading(true);
      await opsAnalyticsDashboardStore.getOrderListTableData({
        pm_adminids: selectedFilters.pm_adminids,
        archived: selectedFilters.archived,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        page: selectedFilters.page,
        limit: selectedFilters.limit,
        search: selectedFilters.search ?? undefined,
        sortBy: selectedFilters.sortBy,
        sortType: selectedFilters.sortType,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 1.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.type) {
      case "pm_adminids":
        setFilters((prev) => ({
          ...prev,
          pm_adminids:
            filters.pm_adminids?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "archived":
        setFilters((prev) => ({
          ...prev,
          archived:
            prev.archived?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_adminids: [],
      order_id: [],
      order_type: [],
      archived: [],
    }));

    setSelectedFiltersData([]);
  };

  const colorsdataQcApproved = [
    theme.palette.mintGreen?.main,
    theme.palette.customColors.purple,
  ];

  const iconsourceingOps = [prCreated, poCreated, prCreated];

  const iconMock = [
    mockuplod,
    mockapproval,
    mockpntr,
    mockuplod,
    mockapproval,
    mockuplod,
  ];

  const piecolor = [
    theme.palette.mintGreen?.main,
    theme.palette.goldenYellow?.main,
    theme.palette.skyBlue?.main,
    theme.palette.orange?.main,
    theme.palette.blue?.main,
    theme.palette.purple?.main,
    theme.palette.green?.main,
  ];

  const getImage = (label: string) => {
    try {
      return require(`../../../assets/${label
        .toLowerCase()
        .replace(/\s+/g, "_")}.png`);
    } catch (error) {
      return require("../../../assets/Defaulticon.png");
    }
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (searchText.length > 2) {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
  };
  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const stepImagesInventoryOpsTat = [
    rawInward,
    rawQc,
    printOutward,
    printInward,
    printQc,
  ];
  const colorsInventoryOpsTat = [
    theme.palette.green?.main,
    theme.palette.goldenYellow?.main,
    theme.palette.purple?.main,
    theme.palette.orange?.main,
    theme.palette.skyBlue?.main,
  ];

  const data_keys_config = [
    {
      key: "raw_inward_tat",
      color: theme.palette.green?.main,
      activeColor: theme.palette.green?.dark,
    },
    {
      key: "raw_inward_tat_qc",
      color: theme.palette.goldenYellow?.main,
      activeColor: theme.palette.goldenYellow?.dark,
    },
    {
      key: "print_outward_tat",
      color: theme.palette.purple?.main,
      activeColor: theme.palette.purple?.dark,
    },
    {
      key: "print_inward_tat",
      color: theme.palette.orange?.main,
      activeColor: theme.palette.orange?.dark,
    },
    {
      key: "print_inward_tat_qc",
      color: theme.palette.skyBlue?.main,
      activeColor: theme.palette.skyBlue?.dark,
    },
  ];

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_OPS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box sx={{ margin: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                // backgroundColor: theme.palette.mode === "dark" ? "#2B2B2B" : theme.palette.customColors.greyBodyBackground,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.customColors.tableGrayHeadingColor
                    : theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ minWidth: "200px", width: "200px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      Date Range
                    </CustomTypography>
                    <DateRangePickerNew
                      onRangeSelect={handleRangeSelect}
                      startDate={filters.start_date}
                      endDate={filters.end_date}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"POC"}
                    </CustomTypography>
                    <AppSearchDropDown2
                      showSelection={false}
                      isMultiSelect={true}
                      title="Select Poc"
                      fontSize="14px"
                      value={filters.pm_adminids}
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      local={false}
                      handleSearch={(pm_adminids) =>
                        handleAdminSearch(pm_adminids!)
                      }
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          pm_adminids: items,
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Order Type"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      valuesList={
                        filters.order_type?.map((item) => item.id) || []
                      }
                      list={
                        projectStore?.orderType?.map((order) => ({
                          id: order.id.toString(),
                          value: order.value,
                        })) || []
                      }
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_type:
                            projectStore?.orderType?.filter((item) =>
                              ids.includes(item.id)
                            ) || [],
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Project Status"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      fontSize="0.7rem"
                      isMultiSelect={true}
                      valuesList={
                        filters.archived?.map((item) => item.id) || []
                      }
                      list={[{ id: "1", value: "Archived" }]}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          archived: ids.map((id) => ({
                            id,
                            value: id === "1" ? "Archived" : "-1",
                          })),
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      Order ID
                    </CustomTypography>

                    <AppSearchDropDown2
                      showSelection={false}
                      isMultiSelect={true}
                      title="Order Id"
                      value={filters.order_id}
                      fontSize="12px"
                      list={orderStore.orderIds.map((item) => {
                        return { id: String(item), value: String(item) };
                      })}
                      local={false}
                      handleSearch={handleOrderIdSearch}
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_id: items,
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Excluded orders"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      isMultiSelect={false}
                      value={filters.excluded_orders?.id || "false"}
                      list={
                        excludedOrdersOptions.map((option) => ({
                          id: option.id,
                          value: option.value,
                        })) || []
                      }
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setFilters((prev) => ({
                          ...prev,
                          excluded_orders:
                            excludedOrdersOptions.find(
                              (option) => option.id === id
                            ) || null,
                        }));
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ marginTop: 3.1 }}>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      disabled={loading}
                    />
                  </Box>

                  <Box sx={{ marginTop: 3.1 }}>
                    <AppButton
                      variant="secondary"
                      label="Clear"
                      customStyles={{ width: "100%" }}
                      onClick={() => handleClearAllFilters()}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ paddingTop: "15px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    borderRadius: 2,
                    paddingLeft: "40px",
                    paddingRight: "40px",
                  }}
                >
                  {selectedFiltersData.map((filter, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.background.paper
                            : theme.palette.customColors.lightGreyColor,
                        borderRadius: 5,
                        border: "1px solid #E2E2E2",
                        height: "30px",
                        paddingRight: "5px",
                      }}
                    >
                      <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.blackBorder}
                        style={{ paddingLeft: "10px", paddingRight: "5px" }}
                      >
                        {filter.value}
                      </CustomTypography>
                      <Button
                        onClick={() => handleRemoveFilter(index)}
                        sx={{
                          width: "14px",
                          height: "14px",
                          minWidth: "14px",
                          minHeight: "14px",
                          fontSize: "15px",
                          padding: "0px",
                          border: "1px solid #E2E2E2",
                          borderRadius: "50%",
                          backgroundColor: theme.palette.customColors.white,
                          cursor: "pointer",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                          },
                        }}
                      >
                        ×
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ marginBottom: 3 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
                style={{ margin: "12px 10px 0px 0px" }}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>
            <Box
              sx={{
                marginBottom: 3,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {opsAnalyticsDashboardStore.ops_tats.map((item, index) => (
                  <Grid2
                    size={{ xl: 2, lg: 3, md: 3, sm: 4, xs: 12 }}
                    key={`${item.label}-${index}`}
                  >
                    <TypeFilterCard
                      title={item.label}
                      value={item.value ?? "0"}
                      color={""}
                      icon={
                        "https://content.swageazy.com/icons/boxes_green.png"
                      }
                      onClick={(type) => {
                        let url = "";
                        switch (type) {
                          case "Total Inwards":
                            url = `/report/data?title=${encodeURI(
                              "Total Inwards"
                            )}&base_url=/v5/ops-analytics&path=/data/inward&${searchParams.toString()}`;
                            window.open(url, "_blank");
                            break;
                        }
                      }}
                    />
                  </Grid2>
                ))}
              </Grid2>
            </Box>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 4,
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              <Box sx={{ width: { xs: "100%", lg: "70%" } }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: { xs: "wrap", lg: "nowrap" },
                    gap: 2,
                  }}
                >
                  {opsAnalyticsDashboardStore.dispatch_delivery_received_supply_tats?.map(
                    (item, index) => (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          flex: "1 1 200px",
                          maxWidth: "300px",

                          "@media (min-width: 920px)": {
                            maxWidth: "calc(33.33% - 16px)",
                          },
                          "@media (max-width: 920px)": {
                            maxWidth: "calc(50% - 16px)",
                          },
                          "@media (max-width: 600px)": {
                            maxWidth: "100%",
                          },
                        }}
                      >
                        <TypeFilterCard
                          title={item.label}
                          message={item.info}
                          value={item.value ?? "0"}
                          color={
                            "https://content.swageazy.com/icons/boxes_green.png"
                          }
                          icon={getImage(item.label)}
                          onClick={(title) => {
                            switch (title) {
                              case "Dispatch Tat":
                                const dipatch_url = `/report/data?title=${encodeURI(
                                  "Dispatch Tat"
                                )}&base_url=/v5/ops-analytics&path=/data/dispatch&${searchParams.toString()}`;
                                window.open(dipatch_url, "_blank");
                                break;
                              case "Delivery Tat":
                                const delivery_url = `/report/data?title=${encodeURI(
                                  "Delivery Tat"
                                )}&base_url=/v5/ops-analytics&path=/data/delivery&${searchParams.toString()}`;
                                window.open(delivery_url, "_blank");
                                break;
                              case "Received Supply Tat":
                                const received_supply_url = `/report/data?title=${encodeURI(
                                  "Received Supply Tat"
                                )}&base_url=/v5/ops-analytics&path=/data/received-supply&${searchParams.toString()}`;
                                window.open(received_supply_url, "_blank");
                                break;
                              default:
                                break;
                            }
                          }}
                        />
                      </Box>
                    )
                  )}
                </Box>

                <Box sx={{ paddingTop: 4 }}>
                  <LineChartComponent
                    height={390}
                    message="Warehouse Ops Metrics"
                    title="Warehouse Ops Metrics"
                    data={
                      opsAnalyticsDashboardStore.dispatch_delivery_received_supply_graph ??
                      []
                    }
                    colors={[
                      theme.palette.blue?.main,
                      theme.palette.red?.main,
                      theme.palette.orange?.main,
                      theme.palette.skyBlue?.main,
                    ]}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  width: { xs: "100%", lg: "27%", xl: "27%" },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <PieChartComponent
                  title={
                    opsAnalyticsDashboardStore.response?.orders_count_by_type
                      ? `Order Type (${opsAnalyticsDashboardStore.response.orders_count_by_type.reduce(
                          (acc, curr) => acc + curr.orders_count,
                          0
                        )})`
                      : "Order Type"
                  }
                  data={
                    opsAnalyticsDashboardStore.response?.orders_count_by_type ??
                    []
                  }
                  colors={piecolor}
                  height={290}
                  outerRadius={100}
                  showLegend={true}
                  showTooltip={true}
                />

                <Card sx={{ marginTop: 4, padding: 3, height: 255 }}>
                  <CustomTypography variant="heading5">
                    QC Rejections
                  </CustomTypography>
                  <Divider sx={{ marginY: "1vh" }} />
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {opsAnalyticsDashboardStore.inward_data.map(
                        (item, index) => (
                          <Box key={index} textAlign="center">
                            <CustomTypography
                              variant="heading1"
                              style={{
                                color: colorsdataQcApproved[index],
                                paddingTop: 30,
                                paddingBottom: 10,
                              }}
                            >
                              {item.value}
                            </CustomTypography>
                            <CustomTypography
                              variant="heading8"
                              fontWeight={700}
                              style={{ paddingTop: 20 }}
                            >
                              {item.label}
                            </CustomTypography>
                          </Box>
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 4,
                paddingTop: 4,
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              <Box sx={{ width: { xs: "100%", lg: "70%" } }}>
                <AppStackChart
                  title="Inventory Operations"
                  data={opsAnalyticsDashboardStore.inventory_ops_graph}
                  data_keys_config={data_keys_config}
                  message="sample message"
                  layout="vertical"
                  barSize={55}
                  fontSize="0.9rem"
                  legendFontSize="0.9rem"
                  scrollable={false}
                  sx={{ p: 4 }}
                />
              </Box>

              <Card
                sx={{
                  width: { xs: "100%", lg: "27%" },
                  height: "527px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    p: 4,
                  }}
                >
                  <CustomTypography
                    variant="heading4"
                    color={theme.palette.customColors.blackBorder}
                  >
                    Inventory Ops TAT
                  </CustomTypography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 20,
                        top: 20,
                        width: 2,
                        height: `calc(100% - 40px)`,
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                        zIndex: -1,
                      }}
                    />
                    {opsAnalyticsDashboardStore.inventory_ops_tats?.map(
                      (step, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 3,
                            mt: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 45,
                              height: 45,
                              mr: 2,
                            }}
                          >
                            <img
                              src={stepImagesInventoryOpsTat[index]}
                              alt={step.label}
                              style={{
                                width: "80%",
                                height: "80%",
                                objectFit: "contain",
                                borderRadius: "50%",
                                border: "2px solid #F3F4F6",
                                backgroundColor:
                                  theme.palette.customColors.greyBodyBackground,
                              }}
                            />
                            {index <
                              opsAnalyticsDashboardStore.inventory_ops_tats
                                ?.length -
                                1 && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: "50%",
                                  bottom: -28,
                                  width: 2,
                                  height: 28,
                                  backgroundColor:
                                    theme.palette.customColors.greyTextColor,
                                  transform: "translateX(-50%)",
                                }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start", // Key to prevent height sync
                              width: "100%",
                              gap: 1,
                            }}
                          >
                            <CustomTypography
                              variant="heading6"
                              sx={{ flex: 1 }}
                            >
                              {step.label}
                            </CustomTypography>

                            <Button
                              onClick={() => {
                                switch (step.label) {
                                  case "Raw Inward Tat":
                                    const received_supply_url = `/report/data?title=${encodeURI(
                                      "Raw Inward Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/received-supply&${searchParams.toString()}`;
                                    window.open(received_supply_url, "_blank");
                                    break;
                                  case "Print Outward Tat":
                                    const print_outward_url = `/report/data?title=${encodeURI(
                                      "Print Outward Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/print-outward&${searchParams.toString()}`;
                                    window.open(print_outward_url, "_blank");
                                    break;
                                  case "Print Inward Tat":
                                    const print_inward_url = `/report/data?title=${encodeURI(
                                      "Print Inward Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/print-inward&${searchParams.toString()}`;
                                    window.open(print_inward_url, "_blank");
                                    break;
                                  default:
                                    break;
                                }
                              }}
                              sx={{
                                bgcolor:
                                  colorsInventoryOpsTat[
                                    index % colorsInventoryOpsTat.length
                                  ],
                                // color: theme.palette.customColors.white,
                                color:
                                  theme.palette.mode === "dark"
                                    ? theme.palette.customColors
                                        .blackBodyBackgorund
                                    : theme.palette.customColors.white,
                                fontWeight: "bold",
                                borderRadius: "4px",
                                minWidth: "4rem",
                                maxWidth: "4rem",
                                textAlign: "center",
                                alignSelf: "center",
                                fontSize: "1rem",
                                maxHeight: "28px",
                                padding: 0,
                              }}
                            >
                              {step.value}
                            </Button>
                          </Box>
                        </Box>
                      )
                    )}
                  </Box>
                </Box>
              </Card>
            </Box>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 4,
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              <Box sx={{ width: { xs: "100%", lg: "70%" } }}>
                <Box sx={{ paddingTop: 4 }}>
                  <AppCombinedChart
                    title="Weekly Operations Metrics"
                    data={
                      opsAnalyticsDashboardStore.shipments_projects_inward_graph
                    }
                    message="sample message"
                    barDataKey="inward"
                    areaDataKeys={[
                      { key: "projects", color: theme.palette.skyBlue?.main },
                      { key: "shipments", color: theme.palette.orange?.main },
                    ]}
                    barSize={20}
                    height={400}
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
                <Box sx={{ paddingTop: 4 }}>
                  <AreaChartComponent
                    height={400}
                    message="Mockup Metrics"
                    title="Mockup Metrics"
                    data={opsAnalyticsDashboardStore.mockup_graph}
                    colors={[
                      theme.palette.orange?.light,
                      theme.palette.mintGreen?.light,
                      theme.palette.skyBlue?.light,
                    ]}
                  />
                </Box>
              </Box>
              <Box sx={{ width: { xs: "100%", lg: "27%", xl: "27%" } }}>
                <Box sx={{ paddingTop: 4 }}>
                  <Card
                    sx={{
                      width: "100%",
                      height: "450px",
                      paddingLeft: "32px",
                      paddingRight: "32px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "32px",
                      }}
                    >
                      <CustomTypography
                        variant="heading4"
                        color={theme.palette.customColors.blackBorder}
                      >
                        Sourcing Ops TAT
                      </CustomTypography>
                      <Tooltip title="This section provides insights into Sourcing Ops Turnaround Time">
                        <InfoOutlinedIcon
                          sx={{
                            cursor: "pointer",
                            color: theme.palette.grey[600],
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        paddingTop: "25px",
                      }}
                    >
                      {opsAnalyticsDashboardStore.prpo_tats?.map(
                        (item, index) => (
                          <Box key={index}>
                            <TypeFilterCard
                              title={item.label}
                              value={item.value}
                              message={item.info}
                              variant="linear"
                              color={""}
                              icon={iconsourceingOps[index]}
                              onClick={(title) => {
                                switch (title) {
                                  case "PR Creation Tat":
                                    const dipatch_url = `/report/data?title=${encodeURI(
                                      "PR Creation Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/pr/create&${searchParams.toString()}`;
                                    window.open(dipatch_url, "_blank");
                                    break;
                                  case "PR Approval Tat":
                                    const delivery_url = `/report/data?title=${encodeURI(
                                      "PR Approval Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/pr/approval&${searchParams.toString()}`;
                                    window.open(delivery_url, "_blank");
                                    break;
                                  case "PO Creation Tat":
                                    const received_supply_url = `/report/data?title=${encodeURI(
                                      "PO Creation Tat"
                                    )}&base_url=/v5/ops-analytics&path=/data/po/create&${searchParams.toString()}`;
                                    window.open(received_supply_url, "_blank");
                                    break;
                                  default:
                                    break;
                                }
                              }}
                            />
                          </Box>
                        )
                      )}
                    </Box>
                  </Card>
                </Box>
                <Box sx={{ paddingTop: 4 }}>
                  <Card sx={{ width: "100%", height: "605px" }}>
                    <Box sx={{ m: 4 }}>
                      <CustomTypography
                        variant="heading4"
                        style={{ paddingBottom: 15 }}
                        color={theme.palette.customColors.blackBorder}
                      >
                        Mockup TAT
                      </CustomTypography>
                      {opsAnalyticsDashboardStore?.mockup_tats?.length > 0 && (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 3fr)",
                            gap: 1,
                          }}
                        >
                          {opsAnalyticsDashboardStore.mockup_tats.map(
                            (item, index) => (
                              <Box
                                key={`${item.label}-${index}`}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  mx: "auto",
                                  mt: 4,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: "50%",
                                  }}
                                >
                                  <img
                                    src={iconMock[index]}
                                    alt={`icon-${index}`}
                                    style={{ width: 60, height: 60 }}
                                  />
                                </Box>
                                <CustomTypography
                                  variant="heading8"
                                  style={{
                                    marginBottom: 10,
                                    marginTop: 10,
                                  }}
                                >
                                  {item.label}
                                </CustomTypography>
                                <AppNoFocusButton
                                  onClick={() => {
                                    switch (item.label) {
                                      case "Mockup Upload":
                                      case "Mockup Approval":
                                        const mockup_url = `/report/data?title=${encodeURI(
                                          "Mockup Tats"
                                        )}&base_url=/v5/ops-analytics&path=/data/mockups&${searchParams.toString()}`;
                                        window.open(mockup_url, "_blank");
                                        break;
                                      case "Mockup To Printer":
                                        const mockup_sent_to_printer_url = `/report/data?title=${encodeURI(
                                          "Mockup Sent To Printer Tat"
                                        )}&base_url=/v5/ops-analytics&path=/data/mockups-to-printer&${searchParams.toString()}`;
                                        window.open(
                                          mockup_sent_to_printer_url,
                                          "_blank"
                                        );
                                        break;
                                    }
                                  }}
                                >
                                  <CustomTypography variant="heading4">
                                    {item.value}
                                  </CustomTypography>
                                </AppNoFocusButton>
                              </Box>
                            )
                          )}
                        </Box>
                      )}
                      {opsAnalyticsDashboardStore?.mockup_tats?.length ===
                        0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "608px",
                          }}
                        >
                          <CustomTypography variant="heading8">
                            Nothing to show for the selected filters
                          </CustomTypography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Box>
              </Box>
            </Box>
            <Box>
              <DashboardOrderTable
                orders={opsAnalyticsDashboardStore.orderlistReponse?.list || []}
                limit={filters.limit}
                maxHeight={600}
                onPageChange={(newPage: number) => {
                  setFilters((prev) => ({ ...prev, page: newPage }));
                }}
                page_no={selectedFilters.page}
                total_pages={
                  opsAnalyticsDashboardStore.orderlistReponse?.total_pages
                }
                total_count={
                  opsAnalyticsDashboardStore.orderlistReponse?.total_count
                }
                onSortChange={(column, sortType) => {
                  const sortByField = column === "id" ? "order_id" : column;

                  setFilters((prev) => ({
                    ...prev,
                    sortBy: sortByField,
                    sortType:
                      prev.sortType === SortType.ASC
                        ? SortType.DESC
                        : SortType.ASC,
                  }));
                }}
                sortBy={selectedFilters.sortBy}
                sortType={selectedFilters.sortType}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(OpsAnalyticsDashboard);
